// src/components/workshop-hub/MyProposedWorkshopsTab.tsx
import { useCallback, useEffect, useState } from "react";
import {
  Container, // Không cần Container ở đây vì nó đã được bọc bởi WorkshopHub
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  TextField, // Có thể không cần các filter nếu My Proposed Workshops ít hơn
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Stack,
  IconButton,
  Paper,
  CircularProgress,
  Pagination,
} from "@mui/material";
import {
  Search as SearchIcon,
  GridView as GridViewIcon,
  CalendarMonth as CalendarIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router";
import { apiRemote } from "@/api";
import useDebounce from "@/hooks/use-debounce";

interface MyProposedWorkshopsTabProps {
  username: string | undefined; // Nhận userId từ component cha
}


// Define the workshop statuses
const STATUS_AWAITING_APPROVAL = "awaiting_approval";
const STATUS_APPROVED = "approved";
const STATUS_REJECTED = "rejected";
const STATUS_REVISION = "revision";
const STATUS_INACTIVE = "inactive";

// Helper function to get Chip color based on status
const getStatusChipColor = (status: string) => {
  switch (status) {
    case STATUS_APPROVED:
      return "success";
    case STATUS_AWAITING_APPROVAL:
    case STATUS_REVISION:
      return "warning";
    case STATUS_REJECTED:
    case STATUS_INACTIVE:
      return "error";
    default:
      return "default";
  }
};

export default function MyProposedWorkshopsTab({ username }: MyProposedWorkshopsTabProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all"); // Có thể chỉ cần filter status
  const [viewMode, setViewMode] = useState<"grid" | "calendar">("grid"); // Có thể giữ mặc định
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [workshops, setWorkshops] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(6);
  const [totalPages, setTotalPages] = useState<number>(1);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const loadMyProposedWorkshops = useCallback(async () => {
    if (!username) { // Đảm bảo có userId trước khi gọi API
      setWorkshops([]);
      setTotalPages(1);
      return;
    }
    setLoading(true);
    try {
      // Giả sử có một API riêng hoặc tham số để lấy workshop của người dùng
      // Ví dụ: apiRemote.getListWorkshop(..., authorId: userId)
      // Hoặc nếu backend có endpoint riêng: apiRemote.getMyProposedWorkshops(userId, ...)
      const response = await apiRemote.getListWorkshopProposal( // Thay đổi API call nếu cần
        currentPage,
        itemsPerPage,
        searchTerm,
        undefined, // category
        selectedStatus === "all" ? undefined : selectedStatus,
        username // Truyền userId làm tham số author_id hoặc tương tự
      );
      if (response.data.data && Array.isArray(response.data.data)) {
        setWorkshops(response.data.data);
        setTotalPages(response.data.last_page);
      } else {
        setWorkshops([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching my proposed workshops:", error);
      setWorkshops([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, debouncedSearchTerm, selectedStatus, username]);

  useEffect(() => {
    loadMyProposedWorkshops();
  }, [loadMyProposedWorkshops]);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const processedWorkshops = workshops.map((workshop) => {
    let tags = [];
    try {
      tags = workshop.topic ? JSON.parse(workshop.topic).map((tag: any) => tag.name) : [];
    } catch (e) {
      console.error("Failed to parse topic:", workshop.topic, e);
      tags = [];
    }

    const formattedDate = new Date(workshop.date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

    return {
      ...workshop,
      host: workshop.host_name,
      image: workshop.image_url,
      tags: tags,
      date: formattedDate,
      is_registered: workshop.is_registered, // Vẫn giữ is_registered nếu API trả về (có thể không cần thiết ở đây)
    };
  });

  const workshopsByDate = processedWorkshops.reduce(
    (acc: any, workshop: any) => {
      const date = workshop.date;
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(workshop);
      return acc;
    },
    {}
  );

  return (
    <Box>
      {/* Filter Bar */}
      <Paper sx={{ p: 2, mb: 4 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            fullWidth
            placeholder="Search your proposed workshops..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
              ),
            }}
          />
          {/* Có thể giữ hoặc bỏ các FormControl filter tùy nhu cầu */}
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={selectedStatus}
              label="Status"
              onChange={(e) => setSelectedStatus(e.target.value as string)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="awaiting_approval">Awaiting Approval</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
              <MenuItem value="revision">Revision</MenuItem>
              {/* <MenuItem value="inactive">InActive</MenuItem> */}
            </Select>
          </FormControl>
          <Box
            sx={{
              display: "flex",
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <IconButton
              onClick={() => setViewMode("grid")}
              color={viewMode === "grid" ? "primary" : "default"}
            >
              <GridViewIcon />
            </IconButton>
            <IconButton
              onClick={() => setViewMode("calendar")}
              color={viewMode === "calendar" ? "primary" : "default"}
            >
              <CalendarIcon />
            </IconButton>
          </Box>
        </Stack>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
          <Typography variant="h6" color="text.secondary" sx={{ ml: 2 }}>Loading your workshops...</Typography>
        </Box>
      ) : (
        <>
          {processedWorkshops.length === 0 && !loading ? (
            <Typography variant="h5" color="text.secondary" align="center" sx={{ mt: 4 }}>
              You have not proposed any workshops yet.
            </Typography>
          ) : (
            viewMode === "grid" ? (
              <Grid container spacing={4}>
                {processedWorkshops.map((workshop) => (
                  <Grid item xs={12} sm={6} md={4} key={workshop.id}>
                    <Card
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: 2,
                        transition: "transform 0.2s ease, box-shadow 0.2s ease",
                        "&:hover": {
                          transform: "translateY(-8px)",
                          boxShadow: "0 12px 25px rgba(0,0,0,0.1)",
                        },
                      }}
                    >
                      <CardMedia
                        component="img"
                        height="100%"
                        image={workshop.image}
                        alt={workshop.title}
                        sx={{ borderTopLeftRadius: 8, borderTopRightRadius: 8, objectFit: "fill"}}
                      />
                      <CardContent
                        sx={{
                          flexGrow: 1,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Typography variant="h6" gutterBottom>
                          {workshop.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Host {workshop.user_fullname}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ mb: 2, flexWrap: "wrap" }}
                        >
                          {workshop.tags.map((tag: string) => (
                            <Chip
                              key={tag}
                              label={tag}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ mb: 1 }}
                            />
                          ))}
                        </Stack>
                        {workshop.status && (
                          <Chip
                            label={workshop.status.replace(/_/g, " ").toUpperCase()} // Format status for display
                            size="small"
                            color={getStatusChipColor(workshop.status)}
                            sx={{ mb: 2, alignSelf: "flex-start" }}
                          />
                        )}
                        <Typography
                          variant="body2"
                          sx={{ mb: 2, display: "flex", alignItems: "center" }}
                        >
                          📅 {workshop.date}
                        </Typography>
                        <Button
                          variant="outlined"
                          sx={{ mt: "auto" }}
                          onClick={() => navigate(`./detail-proposal/${workshop.id}`)}
                        >
                          View Details
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              // Calendar View for My Proposed Workshops
              <Box>
                <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
                  My Proposed Workshops (Calendar View)
                </Typography>
                {Object.keys(workshopsByDate).length > 0 ? (
                  Object.keys(workshopsByDate)
                    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
                    .map((date) => (
                      <Box key={date} sx={{ mb: 4 }}>
                        <Typography
                          variant="h5"
                          sx={{
                            mb: 2,
                            borderBottom: 1,
                            borderColor: "divider",
                            pb: 1,
                          }}
                        >
                          🗓️ {date}
                        </Typography>
                        <Grid container spacing={2}>
                          {workshopsByDate[date].map((workshop: any) => (
                            <Grid item xs={12} sm={6} md={4} key={workshop.id}>
                              <Paper
                                elevation={2}
                                sx={{
                                  p: 2,
                                  display: "flex",
                                  flexDirection: "column",
                                  borderRadius: 2,
                                  height: "100%",
                                  transition:
                                    "transform 0.2s ease, box-shadow 0.2s ease",
                                  "&:hover": {
                                    transform: "translateY(-4px)",
                                    boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                                  },
                                  cursor: "pointer",
                                }}
                                onClick={() => navigate(`./detail-proposal/${workshop.id}`)}
                              >
                                <Typography variant="h6" gutterBottom>
                                  {workshop.title}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mb: 1 }}
                                >
                                  Host {workshop.host}
                                </Typography>
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  sx={{ mb: 1, flexWrap: "wrap" }}
                                >
                                  {workshop.tags.map((tag: any) => (
                                    <Chip
                                      key={tag}
                                      label={tag}
                                      size="small"
                                      variant="outlined"
                                    />
                                  ))}
                                </Stack>
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    ))
                ) : (
                  <Typography
                    variant="h5"
                    color="text.secondary"
                    align="center"
                    sx={{ mt: 4 }}
                  >
                    No proposed workshops found for this date range or filters.
                  </Typography>
                )}
              </Box>
            )
          )}

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 4 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}